import { FC, useContext, useEffect, useMemo, useState } from "react";
import { ImageContext } from "@components";
import classNames from "classnames";

import { useProductContext } from "../../../context/ProductContext";
import type { OptionImage } from "../../../services/cms/api";
import { addHeapOptionModalValueChanged } from "../../../services/elevar/events";
import type { MergedOption } from "../../../services/util/optionRendering";
import { getOptionImage } from "../../../util/getOptionImage";
import { getActiveGroup } from "../util/groups";

import type { ClassicGroupPickerProps } from "./ClassicGroupPickerV2";
import Label from "./Label";
import type { OptionPickerProps } from "./OptionPicker";

type Group = NonNullable<MergedOption["groups"]>[number];

export interface RichGroupProps {
  option: MergedOption;
  optionImages: OptionImage[];
  group: Group;
  optionValueReplacement: string;
  groupPicker: FC<ClassicGroupPickerProps>;
  optionPicker: FC<OptionPickerProps>;
  inModal?: boolean;
}

export default function RichGroupV2({
  option: { name, gridColumns, mobileOrder },
  optionImages: internalImages,
  group,
  optionValueReplacement,
  groupPicker: GroupPicker,
  optionPicker: OptionPicker,
  inModal,
}: RichGroupProps) {
  const {
    product: { optionImages: externalImages },
    selectedOptions: externalOptions,
  } = useProductContext();
  const ImageElement = useContext(ImageContext);

  const externalValue =
    externalOptions[name]?.replace(optionValueReplacement, "").trim() ?? "";
  const [selectedValue, setSelectedValue] = useState(externalValue);
  const hasNestedOptions = group.groups?.length || group.values?.length;

  // Sync internal state if external value changes
  useEffect(() => {
    setSelectedValue((current) => externalValue || current);
  }, [externalValue]);

  const activeGroup = group.groups
    ? getActiveGroup(selectedValue, group.groups)
    : null;

  const groupIdentifier = useMemo(() => {
    const getFirstValue = (targetGroup = group): string => {
      if (targetGroup.values?.length) return targetGroup.values[0].value;
      if (targetGroup.groups?.length)
        return getFirstValue(targetGroup.groups[0]);

      return targetGroup.name;
    };

    const getMatchingValue = (targetGroup = group): string | undefined => {
      const groupNameMatchesValue =
        !targetGroup.values?.length &&
        !targetGroup.groups?.length &&
        targetGroup.name === selectedValue;
      const groupContainsMatchingValue = targetGroup.values?.some(
        (option) => option.value === selectedValue
      );
      const subgroupContainsMatchingValue =
        targetGroup.groups?.some(getMatchingValue);

      if (
        groupNameMatchesValue ||
        groupContainsMatchingValue ||
        subgroupContainsMatchingValue
      )
        return selectedValue;

      return undefined;
    };

    const matchingValue = getMatchingValue();
    const firstValue = getFirstValue();
    return matchingValue || firstValue;
  }, [group, selectedValue]);

  const displayValue = activeGroup ? selectedValue : groupIdentifier;

  const internalOptions = { [name]: displayValue };
  const combinedOptions = { ...externalOptions, ...internalOptions };

  const internalImage = getOptionImage(internalImages, internalOptions);
  const externalImage = getOptionImage(externalImages, combinedOptions);
  const matchingImage = internalImage ?? externalImage;

  const handleChange = (_groupName: string, value: string) => {
    if (inModal) addHeapOptionModalValueChanged(group.name, value);

    setSelectedValue(value);
  };

  return (
    <div className="flex flex-col gap-6 shrink-0 w-60 md:w-[248px]">
      <div className="relative w-full aspect-square bg-flint">
        {matchingImage && (
          <ImageElement src={matchingImage.image.src} alt={group.name} />
        )}
      </div>

      <div
        className={classNames("flex flex-col flex-1 [text-wrap:balance]", {
          "gap-4": !inModal,
          "gap-6": inModal,
        })}
      >
        <div className="flex flex-col gap-1.5">
          <p className="font-medium uppercase text-input text-slate">
            {group.label || group.name}
          </p>
          {group.description && <p>{group.description}</p>}
        </div>

        {hasNestedOptions && (
          <div className="flex flex-col gap-4">
            {group.groups?.length ? (
              <GroupPicker
                isNested
                fullWidth
                gridColumns={group.gridColumns || gridColumns}
                mobileOrder={mobileOrder}
                onChange={handleChange}
                label={group.title}
                name={group.name}
                groups={group.groups}
                selectedValue={displayValue}
                modal={null}
                inModal={inModal}
              />
            ) : (
              <>
                <Label>
                  {group.title}:{" "}
                  <span className="normal-case">{displayValue}</span>
                </Label>
                <OptionPicker
                  fullWidth
                  type={group.type}
                  gridColumns={group.gridColumns || gridColumns}
                  mobileOrder={mobileOrder}
                  onChange={handleChange}
                  name={group.name}
                  selectedValue={displayValue}
                  // Groups can have either nested groups or values (based on `hasNestedOptions`)
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  values={group.values!}
                  inModal={inModal}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
